import {
  useCallback, useContext, useEffect, useState,
} from 'react';
import usePageMeta from '../../hooks/usePageMeta';
import { AuthContext } from '../../context/AuthContext';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import AnimationPreview from '../../components/sprite-editor/AnimationPreview';
import {
  loadSheet, emptySheet, SHEET_W, SHEET_H,
} from '../../components/sprite-editor/spriteData';
import {
  listSubmissions,
  getSubmission,
  approveSubmission,
  rejectSubmission,
  getSettings,
  setSubmissionsOpen,
} from '../../services/sprites';

const STATUS_STYLES = {
  pending: 'bg-amber-900/40 text-amber-300',
  approved: 'bg-emerald-900/40 text-emerald-300',
  rejected: 'bg-red-900/40 text-red-300',
};

/**
 * Sprite submission review queue.
 *
 * The selected submission is shown as a looping walk cycle, not a static
 * grid. Judging whether a walk reads correctly from four still frames is
 * close to impossible — it is the reason the preview exists in the editor and
 * the reason it is reused here.
 */
function SpriteReview() {
  const { token } = useContext(AuthContext);
  usePageMeta({ title: 'Sprite submissions', canonicalPath: '/admin/sprites' });

  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [frames, setFrames] = useState(emptySheet);
  const [previewRow, setPreviewRow] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [list, settings] = await Promise.all([
        listSubmissions(token),
        getSettings(token),
      ]);
      setSubmissions(list.submissions || []);
      setOpen(Boolean(settings.spriteSubmissionsOpen));
    } catch (err) {
      setStatus({ kind: 'error', text: err.message || 'Failed to load submissions.' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSelect = async (summary) => {
    setSelected(summary);
    setReviewNote('');
    try {
      // The list omits image data, so the sheet is fetched only when opened.
      const full = await getSubmission(token, summary.id);
      setFrames(await loadSheet(full.png));
    } catch (err) {
      setStatus({ kind: 'error', text: err.message || 'Could not load that sheet.' });
      setFrames(emptySheet());
    }
  };

  const review = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      const fn = action === 'approve' ? approveSubmission : rejectSubmission;
      await fn(token, selected.id, reviewNote);
      setStatus({
        kind: 'ok',
        text: action === 'approve'
          ? 'Approved. Download the PNG, refine it in Aseprite, and commit it.'
          : 'Rejected.',
      });
      setSelected(null);
      await refresh();
    } catch (err) {
      setStatus({ kind: 'error', text: err.message || `Failed to ${action}.` });
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!selected) return;
    const full = await getSubmission(token, selected.id);
    const link = document.createElement('a');
    link.href = full.png;
    link.download = `${selected.targetSheet}-${selected.id.slice(-6)}.png`;
    link.click();
  };

  const handleToggleOpen = async () => {
    try {
      const next = !open;
      await setSubmissionsOpen(token, next);
      setOpen(next);
      setStatus({
        kind: 'ok',
        text: next
          ? 'Submissions are open to every logged-in player.'
          : 'Submissions are limited to contributors.',
      });
    } catch (err) {
      setStatus({ kind: 'error', text: err.message || 'Failed to update setting.' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const pending = submissions.filter((s) => s.status === 'pending');
  const reviewed = submissions.filter((s) => s.status !== 'pending');

  return (
    <div className="min-h-screen bg-gray-950 py-8">
      <div className="mx-auto max-w-6xl px-6">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Sprite submissions</h1>
            <p className="text-sm text-gray-400 mt-1">
              {`${pending.length} awaiting review`}
            </p>
          </div>

          <label
            htmlFor="submissions-open"
            className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer"
          >
            <input
              id="submissions-open"
              type="checkbox"
              checked={open}
              onChange={handleToggleOpen}
              className="rounded border-gray-600"
            />
            Open submissions to all players
          </label>
        </header>

        {status && (
          <div
            className={`mb-4 rounded-md px-4 py-2 text-sm ${
              status.kind === 'error' ? 'bg-red-900/40 text-red-300' : 'bg-emerald-900/40 text-emerald-300'
            }`}
          >
            {status.text}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <section className="space-y-2">
            {submissions.length === 0 && (
              <p className="text-sm text-gray-500 rounded-lg border border-dashed border-gray-800 p-8 text-center">
                No submissions yet.
              </p>
            )}

            {[...pending, ...reviewed].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => handleSelect(s)}
                className={`w-full text-left rounded-lg border p-4 transition-colors ${
                  selected?.id === s.id
                    ? 'border-primary bg-gray-900'
                    : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-white">{s.submitterName}</span>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${STATUS_STYLES[s.status]}`}>
                    {s.status}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {`${s.targetSheet} · ${new Date(s.createdAt).toLocaleString()}`}
                </div>
                {s.note && <p className="text-xs text-gray-400 mt-2">{s.note}</p>}
                {s.reviewNote && (
                  <p className="text-xs text-gray-500 mt-1 italic">
                    {`Review: ${s.reviewNote}`}
                  </p>
                )}
              </button>
            ))}
          </section>

          <aside className="space-y-4">
            {selected ? (
              <>
                <AnimationPreview frames={frames} row={previewRow} />

                <div className="flex gap-1">
                  {['down', 'left', 'right', 'up'].map((facing, i) => (
                    <button
                      key={facing}
                      type="button"
                      onClick={() => setPreviewRow(i)}
                      className={`flex-1 px-2 py-1 text-[10px] rounded ${
                        previewRow === i
                          ? 'bg-primary text-navy font-semibold'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {facing}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleDownload}
                  className="w-full px-3 py-2 text-xs rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
                >
                  {`Download PNG (${SHEET_W}x${SHEET_H})`}
                </button>

                {selected.status === 'pending' && (
                  <div className="space-y-2 pt-2 border-t border-gray-800">
                    <label htmlFor="review-note" className="block">
                      <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
                        Note to submitter
                      </span>
                      <textarea
                        id="review-note"
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        rows={3}
                        maxLength={1000}
                        className="w-full rounded bg-gray-800 border border-gray-700 text-xs text-gray-200 p-2"
                      />
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => review('approve')}
                        className="flex-1 px-3 py-2 text-xs font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => review('reject')}
                        className="flex-1 px-3 py-2 text-xs font-semibold rounded bg-red-700 text-white hover:bg-red-600 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-500">
                      Approving records the decision and hands you the file — it
                      does not change the live game. Commit the PNG to publish.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500 rounded-lg border border-dashed border-gray-800 p-8 text-center">
                Select a submission to preview it.
              </p>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

export default SpriteReview;
